/**
 * Staff-managed reference data: custom carriers and lines of business.
 * Built-in defaults live in @commpass/shared; these are runtime additions.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export interface CarrierRow {
  id: string;
  name: string;
  link: string | null;
}
export interface LineRow {
  id: string;
  name: string;
}

export function useCarriers() {
  return useQuery<CarrierRow[]>({
    queryKey: ['carriers'],
    queryFn: async () => {
      const { data, error } = await supabase.from('carriers').select('id, name, link').order('name');
      if (error) throw error;
      return (data ?? []) as CarrierRow[];
    },
  });
}

export function useAddCarrier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; link: string }) => {
      const { error } = await supabase.from('carriers').insert({ name: input.name, link: input.link || null });
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['carriers'] }),
  });
}

export function useLines() {
  return useQuery<LineRow[]>({
    queryKey: ['lines'],
    queryFn: async () => {
      const { data, error } = await supabase.from('lines').select('id, name').order('name');
      if (error) throw error;
      return (data ?? []) as LineRow[];
    },
  });
}

export function useAddLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string }) => {
      const { error } = await supabase
        .from('lines')
        .insert({ id: input.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'), name: input.name });
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lines'] }),
  });
}
